import { useState, type ReactNode } from 'react';
import { Box, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { GAUGE_STYLES, type GaugeStyle } from '@casa/model';
import { CanvasView, TILE_FONT, drawElements, gradientColors, valueTileElements, type ValueTileContent } from '@casa/tiles';
import { ScreenRow } from '../forms/form_rows.tsx';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';

// The gauge's style (docs/specs/gauge-styles.md), kept in the tile style: a row opening a screen with
// a picture of each style, drawn as the tile draws it, with a sample reading. A tile's is edited in
// its gauge's options, the dashboard's in its tile style (issue #24).

export const GAUGE_STYLE_NAMES: Readonly<Record<GaugeStyle, string>> = {
    ticks: 'Ticks',
    arc: 'Arc',
    marker: 'Marker',
    needle: 'Needle',
    half: 'Half',
    ring: 'Ring',
    rings: 'Rings',
    blocks: 'Blocks',
    glow: 'Glow',
    bar: 'Bar',
    cells: 'Cells',
    thermometer: 'Thermometer',
    tank: 'Tank',
    bullet: 'Bullet',
    dots: 'Dots',
    minimal: 'Minimal',
};

/** A sample gauge: 22.4 on a scale from 10 to 30, with a second value of 21.5 on the same scale. */
function sample(style: GaugeStyle) : ValueTileContent {
    return {
        numberText: '22.4',
        unitText: '°C',
        unitScale: 1,
        color: '#ffffff',
        gauge: {
            value: 22.4,
            min: 10,
            max: 30,
            colors: gradientColors('blue-yellow-red'),
            iconColor: '#ffffff',
            secondaryText: '21.5',
            secondaryColor: '#cccccc',
            secondaryValue: 21.5,
            marksSecond: true,
            style,
            more: style === 'rings' ? [{ value: 14, min: 10, max: 30, text: '14' }] : [],
            look: {},
        },
    };
}

function Preview({ style }: { style: GaugeStyle }) : ReactNode {
    return (
        <Box position="relative" width="100%" height="88px" bg="#151515" borderRadius="md" data-gauge-preview={style}>
            <CanvasView deps={[style]} draw={(ctx, width, height) => drawElements(ctx, { left: 6, top: 6, width: width - 12, height: height - 12 }, valueTileElements(sample(style), TILE_FONT))} />
        </Box>
    );
}

export interface GaugeStyleRowProps {
    /** "Gauge style" in a tile style; "Style" in the gauge's own options. */
    label?: string;
    value: GaugeStyle | undefined;
    /** What it is when left out: the dashboard's, else `ticks`. */
    fallback: GaugeStyle;
    onChange: (style: GaugeStyle | undefined) => void;
}

export function GaugeStyleRow({ label = 'Gauge style', value, fallback, onChange }: GaugeStyleRowProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const pick = (style: GaugeStyle | undefined) : void => {
        onChange(style);
        setOpen(false);
    };

    return (
        <ScreenRow
            label={label}
            value={value ? GAUGE_STYLE_NAMES[value] : `Default (${GAUGE_STYLE_NAMES[fallback]})`}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="5">
                    <SettingsGroup>
                        <SettingsRow title={`Default (${GAUGE_STYLE_NAMES[fallback]})`} isCurrent={value === undefined} opens={false} onClick={() => pick(undefined)} />
                    </SettingsGroup>
                    <SimpleGrid columns={{ base: 2, md: 3 }} gap="3">
                        {GAUGE_STYLES.map((style) => (
                            <Box
                                as="button"
                                key={style}
                                aria-label={GAUGE_STYLE_NAMES[style]}
                                onClick={() => pick(style)}
                                textAlign="center"
                                p="1.5"
                                borderRadius="lg"
                                borderWidth="2px"
                                borderColor={value === style ? 'blue.400' : 'transparent'}
                                _hover={{ bg: 'bg.muted' }}
                                cursor="pointer"
                            >
                                <Preview style={style} />
                                <Text fontSize="sm" mt="1" fontWeight={value === style ? 'semibold' : 'normal'}>
                                    {GAUGE_STYLE_NAMES[style]}
                                </Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Stack>
            }
        />
    );
}
